import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, ExternalLink, ArrowLeft, ChevronRight, Instagram, Facebook } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { CATERERS, catererSlug, type Caterer } from './Catering';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1555244162-803834f70033?w=1200&h=800&fit=crop';

function mapsUrl(c: Caterer) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    c.address || c.location || c.name
  )}`;
}

export default function CateringDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('catering', CATERERS, (c, i) =>
    slugify(c.name || String(i))
  );

  const entry: Caterer | undefined = mc.items.find(
    (c: any) => catererSlug(c.name) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Caterer Not Found | Dallas Desi Scoop"
          description="This catering listing could not be found."
          canonical="/catering-only"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Caterer not found.</p>
        <Link
          to="/catering-only"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Catering
        </Link>
      </div>
    );
  }

  const city = entry.location || entry.address?.split(',').slice(-2, -1)[0]?.trim() || 'DFW';
  const title = `${entry.name} in ${city}, TX | Indian Catering | Dallas Desi Scoop`;
  const description = `${entry.name} offers Indian and South Asian catering services in ${city}, TX. Find address, phone number, and contact info.`;
  const canonicalPath = `/catering-only/${slug}`;

  const addressParts = (entry.address || '').split(',').map((p) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : city;

  const jsonLd = [
    {
      '@type': 'FoodEstablishment',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: 'Indian and South Asian catering service',
      address: entry.address
        ? {
            '@type': 'PostalAddress',
            streetAddress,
            addressLocality: addressLocality || undefined,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : undefined,
      telephone: entry.contactNumber || undefined,
      url: entry.instagram || entry.facebook || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Catering', item: `${BASE_URL}/catering-only` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/catering-only"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Catering
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {(entry.address || entry.location) && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address || entry.location}
              </span>
            )}
            <span className="text-white/40">·</span>
            <span className="text-white/75 text-[13px]">Catering Service</span>
          </motion.div>

          {entry.award && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.24 }}
              className="mt-4"
            >
              <p className="text-[12px] font-semibold text-[#f59e0b] tracking-wide">
                {entry.award}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.name} is a catering business serving the South Asian community across Dallas–Fort Worth
            {entry.location ? `, based in ${entry.location}` : ''}.
            {entry.tagline ? ` ${entry.tagline}.` : ''}
            {entry.address ? ` Located at ${entry.address}.` : ''}
          </p>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
              <span>{entry.address || entry.location || '[Coming soon]'}</span>
            </div>
            {entry.contactNumber && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a href={`tel:${entry.contactNumber.replace(/[^\d+]/g, '')}`} className="hover:underline">
                  {entry.contactNumber}
                </a>
              </div>
            )}
          </div>
          <div className="mt-5 flex flex-col gap-2">
            {entry.contactNumber && (
              <a
                href={`tel:${entry.contactNumber.replace(/[^\d+]/g, '')}`}
                className="flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            )}
            {entry.instagram && (
              <a
                href={entry.instagram}
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}
                className="flex items-center justify-center gap-1.5 hover:opacity-90 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-opacity"
              >
                <Instagram className="w-4 h-4" />
                Instagram
              </a>
            )}
            {entry.facebook && (
              <a
                href={entry.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#1877F2] hover:bg-[#1364d4] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </a>
            )}
            {entry.address && (
              <a
                href={mapsUrl(entry)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Get Directions
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
